import {
  endOfMonth,
  endOfYear,
  format,
  isSameDay,
  isSameMonth,
  parse,
  startOfMonth,
  startOfYear,
  subDays,
  subMonths,
  subYears,
} from 'date-fns'

import type { FormField } from '@/types/api/form'
import type {
  RecordFieldOperator,
  RecordFilterMatch,
  RecordSearchRequest,
  RecordTagMode,
} from '@/types/api/record'
import { FormFieldType, RecordSourceType } from '@/types/enums'

/** One chip of the record filter bar. Kept small so the whole list fits in a URL param. */
export type RecordFilter =
  | { kind: 'source'; values: RecordSourceType[] }
  | { kind: 'tags'; ids: string[]; mode: RecordTagMode }
  | { kind: 'created' | 'updated'; from?: string; to?: string }
  | { kind: 'mine' }
  | { kind: 'field'; key: string; op: RecordFieldOperator; value?: string; values?: string[] }

export type FieldKind = 'number' | 'date' | 'choice' | 'toggle' | 'file' | 'text'

export function fieldKind(type: FormFieldType | string): FieldKind {
  switch (type) {
    case FormFieldType.NUMBER:
    case FormFieldType.CURRENCY:
    case FormFieldType.RANGE:
    case FormFieldType.RATING:
    case FormFieldType.PERCENT:
      return 'number'
    case FormFieldType.DATE:
      return 'date'
    case FormFieldType.SELECT:
    case FormFieldType.RADIO:
    case FormFieldType.CHECKBOX:
      return 'choice'
    case FormFieldType.TOGGLE:
      return 'toggle'
    case FormFieldType.FILE_UPLOAD:
      return 'file'
    default:
      return 'text'
  }
}

export const OPERATOR_LABELS: Record<RecordFieldOperator, string> = {
  EQ: 'is',
  NEQ: 'is not',
  CONTAINS: 'contains',
  NOT_CONTAINS: 'does not contain',
  STARTS_WITH: 'starts with',
  GT: 'greater than',
  GTE: 'at least',
  LT: 'less than',
  LTE: 'at most',
  BETWEEN: 'between',
  IN: 'is any of',
  IS_EMPTY: 'is empty',
  IS_NOT_EMPTY: 'is not empty',
}

const DATE_OPERATOR_LABELS: Partial<Record<RecordFieldOperator, string>> = {
  EQ: 'on',
  LT: 'before',
  GT: 'after',
  BETWEEN: 'between',
}

export function operatorLabel(op: RecordFieldOperator, kind: FieldKind): string {
  return (kind === 'date' && DATE_OPERATOR_LABELS[op]) || OPERATOR_LABELS[op]
}

/** Operators that make sense for a field type (first one is the default). */
export function operatorsFor(kind: FieldKind): RecordFieldOperator[] {
  switch (kind) {
    case 'number':
      return ['EQ', 'NEQ', 'GT', 'GTE', 'LT', 'LTE', 'BETWEEN', 'IS_EMPTY', 'IS_NOT_EMPTY']
    case 'date':
      return ['EQ', 'LT', 'GT', 'BETWEEN', 'IS_EMPTY', 'IS_NOT_EMPTY']
    case 'choice':
      return ['IN', 'IS_EMPTY', 'IS_NOT_EMPTY']
    case 'toggle':
      return ['EQ']
    case 'file':
      return ['IS_NOT_EMPTY', 'IS_EMPTY']
    default:
      return ['CONTAINS', 'NOT_CONTAINS', 'EQ', 'NEQ', 'STARTS_WITH', 'IS_EMPTY', 'IS_NOT_EMPTY']
  }
}

export function operatorNeedsValue(op: RecordFieldOperator): 'none' | 'one' | 'two' | 'many' {
  if (op === 'IS_EMPTY' || op === 'IS_NOT_EMPTY') return 'none'
  if (op === 'BETWEEN') return 'two'
  if (op === 'IN') return 'many'
  return 'one'
}

/** Choices of a SELECT/RADIO/CHECKBOX field (designer stores options.choices, the API options.values). */
export function fieldChoices(field: Pick<FormField, 'options'>): string[] {
  const opts = (field.options ?? {}) as Record<string, unknown>
  const choices = Array.isArray(opts.values) ? opts.values : opts.choices
  if (!Array.isArray(choices)) return []
  return choices.map((c) => (typeof c === 'string' ? c : String((c as { value?: unknown }).value ?? '')))
}

/** A filter is complete when it carries the values its operator needs; incomplete ones are not sent. */
export function isComplete(filter: RecordFilter): boolean {
  switch (filter.kind) {
    case 'source':
      return filter.values.length > 0
    case 'tags':
      return filter.ids.length > 0
    case 'created':
    case 'updated':
      return !!filter.from || !!filter.to
    case 'mine':
      return true
    case 'field': {
      const need = operatorNeedsValue(filter.op)
      if (need === 'none') return true
      if (need === 'one') return !!filter.value?.trim()
      if (need === 'two') return (filter.values ?? []).filter((v) => v.trim()).length === 2
      return (filter.values ?? []).length > 0
    }
  }
}

/**
 * Field keys whose values the records list shows, in order: fields of complete field filters, then
 * the sort field. The server sends values for exactly these (a plain list carries none).
 */
export function listedFieldKeys(filters: RecordFilter[], sortBy: string): string[] {
  const keys = filters
    .filter((f): f is Extract<RecordFilter, { kind: 'field' }> => f.kind === 'field' && isComplete(f))
    .map((f) => f.key)
  if (sortBy.startsWith('field:')) keys.push(sortBy.slice('field:'.length))
  return [...new Set(keys)]
}

/** Start of a yyyy-mm-dd day in the browser's time zone, as ISO. */
function dayStart(date: string): string {
  return new Date(`${date}T00:00:00`).toISOString()
}

/** End of a yyyy-mm-dd day in the browser's time zone, as ISO. */
function dayEnd(date: string): string {
  return new Date(`${date}T23:59:59.999`).toISOString()
}

/**
 * Builds the search request from the filter chips. Source, date and creator chips narrow the
 * scope; tag and field chips are combined with {@code match}.
 */
export function buildSearchRequest(args: {
  q: string
  filters: RecordFilter[]
  match: RecordFilterMatch
  includeSubgroups: boolean
  currentUserId: string | null
  sort: { by: string; dir: 'ASC' | 'DESC' }
  page: number
  size: number
}): RecordSearchRequest {
  const request: RecordSearchRequest = {
    match: args.match,
    includeSubgroups: args.includeSubgroups || undefined,
    sort: args.sort,
    page: args.page,
    size: args.size,
  }
  if (args.q.trim()) request.q = args.q.trim()

  const fields: NonNullable<RecordSearchRequest['fields']> = []
  for (const filter of args.filters.filter(isComplete)) {
    switch (filter.kind) {
      case 'source':
        request.sources = [...new Set([...(request.sources ?? []), ...filter.values])]
        break
      case 'tags':
        // One tag chip per request; a second one replaces the first (the UI only allows one).
        request.tags = { ids: filter.ids, mode: filter.mode }
        break
      case 'created':
        if (filter.from) request.createdFrom = dayStart(filter.from)
        if (filter.to) request.createdTo = dayEnd(filter.to)
        break
      case 'updated':
        if (filter.from) request.updatedFrom = dayStart(filter.from)
        if (filter.to) request.updatedTo = dayEnd(filter.to)
        break
      case 'mine':
        if (args.currentUserId) request.creatorUserIds = [args.currentUserId]
        break
      case 'field':
        fields.push({
          fieldKey: filter.key,
          op: filter.op,
          value: filter.value,
          values: filter.values,
        })
        break
    }
  }
  if (fields.length > 0) request.fields = fields
  return request
}

export const SOURCE_LABELS: Record<RecordSourceType, string> = {
  [RecordSourceType.INTERNAL_USER]: 'Internal',
  [RecordSourceType.PUBLIC_FORM]: 'Public form',
  [RecordSourceType.CSV_IMPORT]: 'CSV import',
}

/** Short human summary of a chip, e.g. "Amount at least 10". */
export function describeFilter(
  filter: RecordFilter,
  ctx: { fieldLabel: (key: string) => string; fieldKind: (key: string) => FieldKind; tagLabel: (id: string) => string }
): string {
  switch (filter.kind) {
    case 'source':
      return filter.values.length ? `Source: ${filter.values.map((v) => SOURCE_LABELS[v]).join(', ')}` : 'Source'
    case 'tags': {
      if (filter.ids.length === 0) return 'Tags'
      const names = filter.ids.map(ctx.tagLabel).join(', ')
      const mode = filter.mode === 'ALL' ? 'all of' : filter.mode === 'NONE' ? 'none of' : 'any of'
      return `Tags: ${mode} ${names}`
    }
    case 'created':
    case 'updated': {
      const label = filter.kind === 'created' ? 'Created' : 'Updated'
      if (filter.from && filter.to) return `${label} ${filter.from} – ${filter.to}`
      if (filter.from) return `${label} after ${filter.from}`
      if (filter.to) return `${label} before ${filter.to}`
      return label
    }
    case 'mine':
      return 'Created by me'
    case 'field': {
      const label = ctx.fieldLabel(filter.key)
      const kind = ctx.fieldKind(filter.key)
      const op = operatorLabel(filter.op, kind)
      const need = operatorNeedsValue(filter.op)
      if (need === 'none') return `${label} ${op}`
      if (need === 'two') return `${label} ${op} ${(filter.values ?? []).join(' and ')}`
      if (need === 'many') return `${label} ${op} ${(filter.values ?? []).join(', ')}`
      const value = kind === 'toggle' ? (filter.value === 'true' ? 'Yes' : 'No') : filter.value ?? ''
      return `${label} ${op} ${value}`.trim()
    }
  }
}

// ── sorting ───────────────────────────────────────────────────────────────────

/** Whether a record list can be ordered by this field (files and multi-select checkboxes have no single value). */
export function isSortableField(field: Pick<FormField, 'type' | 'options'>): boolean {
  if (fieldKind(field.type) === 'file') return false
  if (field.type === FormFieldType.CHECKBOX) {
    const mode = String((field.options as Record<string, unknown> | null | undefined)?.selectionMode ?? 'MULTIPLE')
    return mode.toUpperCase() === 'SINGLE'
  }
  return true
}

// ── date ranges ───────────────────────────────────────────────────────────────

export type DatePreset =
  | 'today'
  | 'yesterday'
  | 'last7'
  | 'last30'
  | 'thisMonth'
  | 'lastMonth'
  | 'thisYear'
  | 'lastYear'

export const DATE_PRESETS: { id: DatePreset; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'last7', label: 'Last 7 days' },
  { id: 'last30', label: 'Last 30 days' },
  { id: 'thisMonth', label: 'This month' },
  { id: 'lastMonth', label: 'Last month' },
  { id: 'thisYear', label: 'This year' },
  { id: 'lastYear', label: 'Last year' },
]

const DAY = 'yyyy-MM-dd'

export interface DayRange {
  from: string
  to: string
}

function range(from: Date, to: Date): DayRange {
  return { from: format(from, DAY), to: format(to, DAY) }
}

/** Concrete days for a preset, in the browser's time zone. "Last 7 days" includes today. */
export function presetRange(preset: DatePreset, today: Date = new Date()): DayRange {
  switch (preset) {
    case 'today':
      return range(today, today)
    case 'yesterday':
      return range(subDays(today, 1), subDays(today, 1))
    case 'last7':
      return range(subDays(today, 6), today)
    case 'last30':
      return range(subDays(today, 29), today)
    case 'thisMonth':
      return range(startOfMonth(today), endOfMonth(today))
    case 'lastMonth': {
      const last = subMonths(today, 1)
      return range(startOfMonth(last), endOfMonth(last))
    }
    case 'thisYear':
      return range(startOfYear(today), endOfYear(today))
    case 'lastYear': {
      const last = subYears(today, 1)
      return range(startOfYear(last), endOfYear(last))
    }
  }
}

/** First to last day of a "yyyy-MM" month (the value of an `<input type="month">`). */
export function monthRange(month: string): DayRange | null {
  const date = parse(month, 'yyyy-MM', new Date(2000, 0, 1))
  return Number.isNaN(date.getTime()) ? null : range(startOfMonth(date), endOfMonth(date))
}

/** January 1 to December 31 of a year. */
export function yearRange(year: number): DayRange {
  const date = new Date(year, 0, 1)
  return range(startOfYear(date), endOfYear(date))
}

function parseDay(day: string): Date | null {
  const date = parse(day, DAY, new Date(2000, 0, 1))
  return Number.isNaN(date.getTime()) ? null : date
}

/** The "yyyy-MM" month a range covers exactly, or null. */
export function wholeMonthOf(from?: string, to?: string): string | null {
  const start = from ? parseDay(from) : null
  const end = to ? parseDay(to) : null
  if (!start || !end) return null
  return isSameDay(start, startOfMonth(start)) && isSameDay(end, endOfMonth(start)) ? format(start, 'yyyy-MM') : null
}

/** The year a range covers exactly (Jan 1 to Dec 31), or null. */
export function wholeYearOf(from?: string, to?: string): number | null {
  const start = from ? parseDay(from) : null
  const end = to ? parseDay(to) : null
  if (!start || !end) return null
  return isSameDay(start, startOfYear(start)) && isSameDay(end, endOfYear(start)) ? start.getFullYear() : null
}

/** Short label for a range, e.g. "Mar 2026", "1–15 Jan", "2025". Years are shown when not the current one. */
export function describeDateRange(from?: string, to?: string, today: Date = new Date()): string {
  const start = from ? parseDay(from) : null
  const end = to ? parseDay(to) : null
  const withYear = (d: Date, pattern: string) =>
    d.getFullYear() === today.getFullYear() ? format(d, pattern) : `${format(d, pattern)} ${d.getFullYear()}`
  if (start && end) {
    const year = wholeYearOf(from, to)
    if (year !== null) return String(year)
    if (wholeMonthOf(from, to)) return format(start, 'MMM yyyy')
    if (isSameDay(start, end)) return withYear(start, 'd MMM')
    if (isSameMonth(start, end)) return `${format(start, 'd')}–${withYear(end, 'd MMM')}`
    return `${withYear(start, 'd MMM')} – ${withYear(end, 'd MMM')}`
  }
  if (start) return `from ${withYear(start, 'd MMM')}`
  if (end) return `until ${withYear(end, 'd MMM')}`
  return ''
}
